import { useMutation, useQuery, useQueryClient } from "react-query";
import { fetchApi } from "../../../helpers/reactQueryApi";

type ExportFile = {
  id: string;
  extension: string;
};

type ExportStatus = {
  status: "empty" | "initialized" | "in_progress" | "finished" | "error";
  progress: number;
  files: ExportFile[];
  created_at: string;
  can?: { export?: boolean };
};

export default function useExport(
  basePath: string,
  createParams?: Record<string, unknown>,
) {
  const queryClient = useQueryClient();
  const queryKey = `${basePath}/exports/last`;

  const { data: lastExport, isLoading } = useQuery<ExportStatus>(
    queryKey,
    () => fetchApi(queryKey),
    {
      refetchInterval: (data) => {
        if (data?.status === "initialized" || data?.status === "in_progress") {
          return 3000;
        }
        return false;
      },
    },
  );

  const { mutate: startExport, isLoading: isExporting } = useMutation(
    () =>
      fetchApi(`${basePath}/exports`, { method: "POST", body: createParams }),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
    },
  );

  const canExport = lastExport?.can?.export !== false;

  const files = (lastExport?.files || []).map((f) => ({
    id: f.id,
    extension: f.extension,
    fileUrl: `${basePath}/files/${f.id}`,
    downloadUrl: `/api/storage/files/${f.id}/get?download`,
  }));

  const status =
    lastExport?.status === "error" ? "finished" : lastExport?.status || "empty";

  return {
    lastExport,
    startExport,
    isExporting,
    isLoading,
    canExport,
    status: status as "empty" | "initialized" | "in_progress" | "finished",
    progress: lastExport?.progress || 0,
    percentage: (lastExport?.progress || 0) * 100,
    files,
    createdAt: lastExport?.created_at || "",
  };
}
